import { Box, Button, Flex, Heading, Spinner, Text, VStack } from '@chakra-ui/react'
import { useOktaAuth } from '@okta/okta-react'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangleIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import hivemqLogo from '~/assets/03-hivemq-vert.svg'

export const Route = createFileRoute('/login_/callback')({
  component: LoginCallbackPage,
})

function LoginCallbackPage() {
  const { oktaAuth } = useOktaAuth()
  const [error, setError] = useState<string | null>(null)
  const handledRef = useRef(false)

  useEffect(() => {
    // Prevent double handling in strict mode
    if (handledRef.current) return
    handledRef.current = true

    const handleCallback = async () => {
      const searchParams = new URLSearchParams(window.location.search)

      // Check for error in URL
      const errorParam = searchParams.get('error')
      if (errorParam) {
        const errorDesc = searchParams.get('error_description')?.replace(/\+/g, ' ')
        setError(`${errorParam}: ${errorDesc || 'Authentication failed'}`)
        return
      }

      // Check for authorization code
      const code = searchParams.get('code')
      if (code) {
        try {
          // Exchange code for tokens
          const tokens = await oktaAuth.token.parseFromUrl()

          // Store tokens in Okta token manager
          oktaAuth.tokenManager.setTokens(tokens.tokens)

          // Store the access token in localStorage for API calls
          const accessToken = tokens.tokens.accessToken?.accessToken
          if (accessToken) {
            localStorage.setItem('auth_token', accessToken)
          }

          // Redirect to authenticated app home
          window.location.href = '/'
        } catch (err) {
          console.error('Token exchange error:', err)
          setError(err instanceof Error ? err.message : 'Failed to complete authentication')
        }
      }
    }

    handleCallback()
  }, [oktaAuth])

  if (error) {
    return (
      <Flex h="100vh" align="center" justify="center" bg="gray.900">
        <VStack gap={6} maxW="500px" p={8}>
          <Box w="200px">
            <img src={hivemqLogo} alt="HiveMQ" />
          </Box>
          <AlertTriangleIcon size={48} color="var(--chakra-colors-red-400)" />
          <VStack gap={2} textAlign="center">
            <Heading size="lg" color="white">
              Authentication Failed
            </Heading>
            <Text color="gray.400" fontSize="sm">
              {error}
            </Text>
          </VStack>
          <Button w="100%" colorPalette="yellow" onClick={() => (window.location.href = '/login')}>
            Try Again
          </Button>
        </VStack>
      </Flex>
    )
  }

  return (
    <Flex h="100vh" align="center" justify="center" bg="gray.900">
      <VStack gap={4}>
        <Box w="200px">
          <img src={hivemqLogo} alt="HiveMQ" />
        </Box>
        <Spinner size="lg" color="yellow.300" />
        <Text color="white">Completing authentication...</Text>
      </VStack>
    </Flex>
  )
}
